import type { Workspace, Account, HealthBand, HealthModel } from "./types";
export type { HealthModel, HealthFactorWeight } from "./types";

/* Health Score Designer — client mirror of api/src/reporting/healthmodel.ts.
 * A no-code weighting over each account's shared factors: change a weight and
 * the whole book re-scores. Byte-compatible with the API so the offline
 * designer previews the same portfolio impact the server would compute. */

export const DEFAULT_HEALTH_MODEL: HealthModel = {
  factors: [
    { label: "Product adoption", weight: 35, enabled: true },
    { label: "Stakeholder health", weight: 25, enabled: true },
    { label: "Support sentiment", weight: 20, enabled: true },
    { label: "Invoice & renewal", weight: 20, enabled: true },
  ],
};

export const bandFromScore = (h: number): HealthBand => (h >= 70 ? "good" : h >= 45 ? "watch" : "risk");

export interface ScoredFactor { label: string; value: number; weight: number; contribution: number; }
export interface AccountScore { health: number; band: HealthBand; factors: ScoredFactor[]; }

export function scoreWithModel(a: Account, model: HealthModel): AccountScore {
  const wOf = new Map(model.factors.filter((f) => f.enabled).map((f) => [f.label, f.weight]));
  const used = a.scoreFactors.filter((f) => wOf.has(f.label));
  const totalW = used.reduce((s, f) => s + (wOf.get(f.label) ?? 0), 0) || 1;
  const factors: ScoredFactor[] = used.map((f) => {
    const weight = wOf.get(f.label) ?? 0;
    return { label: f.label, value: f.value, weight, contribution: Math.round((f.value * weight) / totalW) };
  });
  const health = Math.round(factors.reduce((s, f) => s + f.value * f.weight, 0) / totalW);
  return { health, band: bandFromScore(health), factors };
}

export interface AccountProjection {
  accountId: string; accountName: string; arr: number; ownerId: string;
  current: number; currentBand: HealthBand; projected: number; projectedBand: HealthBand; delta: number;
}
export interface ModelImpact {
  rows: AccountProjection[];
  summary: {
    avgCurrent: number; avgProjected: number; avgDelta: number; moved: number;
    currentBands: Record<HealthBand, number>; projectedBands: Record<HealthBand, number>;
    atRiskArrCurrent: number; atRiskArrProjected: number;
  };
}

const emptyBands = (): Record<HealthBand, number> => ({ good: 0, watch: 0, risk: 0 });

export function applyHealthModel(ws: Workspace, model: HealthModel): ModelImpact {
  const rows: AccountProjection[] = ws.accounts.map((a) => {
    const base = scoreWithModel(a, DEFAULT_HEALTH_MODEL);
    const s = scoreWithModel(a, model);
    return {
      accountId: a.id, accountName: a.name, arr: a.arr, ownerId: a.ownerId,
      current: base.health, currentBand: base.band, projected: s.health, projectedBand: s.band,
      delta: s.health - base.health,
    };
  }).sort((x, y) => x.delta - y.delta);

  const currentBands = emptyBands(), projectedBands = emptyBands();
  let atRiskArrCurrent = 0, atRiskArrProjected = 0, moved = 0, sumC = 0, sumP = 0;
  for (const r of rows) {
    currentBands[r.currentBand]++; projectedBands[r.projectedBand]++;
    if (r.currentBand === "risk") atRiskArrCurrent += r.arr;
    if (r.projectedBand === "risk") atRiskArrProjected += r.arr;
    if (r.currentBand !== r.projectedBand) moved++;
    sumC += r.current; sumP += r.projected;
  }
  const n = rows.length || 1;
  return {
    rows,
    summary: {
      avgCurrent: Math.round(sumC / n), avgProjected: Math.round(sumP / n),
      avgDelta: Math.round((sumP - sumC) / n), moved, currentBands, projectedBands,
      atRiskArrCurrent, atRiskArrProjected,
    },
  };
}

export function normalizeModel(input: unknown): HealthModel {
  const src = (input as HealthModel)?.factors;
  if (!Array.isArray(src)) return DEFAULT_HEALTH_MODEL;
  const byLabel = new Map(src.map((f) => [f?.label, f]));
  const factors = DEFAULT_HEALTH_MODEL.factors.map((d) => {
    const f = byLabel.get(d.label);
    const weight = Math.max(0, Math.min(100, Math.round(Number(f?.weight ?? d.weight)) || 0));
    return { label: d.label, weight, enabled: f?.enabled !== false };
  });
  return { factors };
}
